// HTTP METHODS CONSTANTS
export const HTTP_METHODS = {
	GET: 'GET',
	POST: 'POST',
	PUT: 'PUT',
	PATCH: 'PATCH',
	DELETE: 'DELETE',
} as const;

export type THttpMethod = (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS];

// AUTH ENDPOINTS
export const AUTH_ENDPOINTS = {
	LOGIN: {
		url: '/api/v1/auth/login',
		method: HTTP_METHODS.POST,
		requiresAuth: false,
	},
	LOGOUT: {
		url: '/api/v1/auth/logout',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE_PROFILE: {
		url: '/api/v1/auth/update-profile',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
	UPDATE_PROFILE_IMAGE: {
		url: '/api/v1/auth/profile-image',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
	CHANGE_PASSWORD: {
		url: '/api/v1/auth/change-password',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	FORGOT_PASSWORD: {
		url: '/api/v1/auth/forget-password',
		method: HTTP_METHODS.POST,
		requiresAuth: false,
	},
	RESET_PASSWORD: (token?: string) => ({
		url: token ? `/api/v1/auth/reset-password/${token}` : '/api/v1/auth/reset-password',
		method: HTTP_METHODS.POST,
		requiresAuth: false,
	}),
	ME: {
		url: '/api/v1/auth/me',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	INTRO_STATUS: {
		url: '/api/v1/auth/intro-status',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	INTRO_COMPLETE: {
		url: '/api/v1/auth/intro-complete',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	FCM_TOKEN: {
		url: '/api/v1/auth/fcm-token',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
} as const;

export const COMPANY_ENDPOINTS = {
	CURRENT: {
		url: '/api/v1/company/current',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CONSTANTS: {
		url: '/api/v1/company/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	UPDATE_CURRENT: {
		url: '/api/v1/company/current',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
	INVOICE_NUMBERING: {
		url: '/api/v1/company/invoice-numbering',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	UPDATE_INVOICE_NUMBERING: {
		url: '/api/v1/company/invoice-numbering',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
	PURCHASE_INVOICE_NUMBERING: {
		url: '/api/v1/company/purchase-invoice-numbering',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	UPDATE_PURCHASE_INVOICE_NUMBERING: {
		url: '/api/v1/company/purchase-invoice-numbering',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
} as const;

// USERS ENDPOINTS
export const USERS_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/users/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/users/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/users/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/users/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	RESET_PASSWORD: (id: string | number) => ({
		url: `/api/v1/users/reset-password/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/users/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	DELETE_READINESS: (id: string | number) => ({
		url: `/api/v1/users/delete-readiness/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	UPDATE_STATUS: (id: string | number) => ({
		url: `/api/v1/users/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_PROFILE: {
		url: '/users/profile',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// DASHBOARD ENDPOINTS
export const DASHBOARD_ENDPOINTS = {
	GET: {
		url: '/api/v1/dashboard',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// REPORTS ENDPOINTS
export const REPORTS_ENDPOINTS = {
	DASHBOARD: {
		url: '/api/v1/reports/dashboard',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (reportKey: string) => ({
		url: `/api/v1/reports/${reportKey}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	EXPORT: (reportKey: string) => ({
		url: `/api/v1/reports/${reportKey}/export`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	BUNDLE_FLOW: (bundleId: string | number) => ({
		url: `/api/v1/reports/bundle_tracking/flow/${bundleId}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
} as const;

// PERMISSION & ROLE ACCESS ENDPOINTS
export const PERMISSION_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/permission',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// ROLE ACCESS ENDPOINTS
export const ROLE_ACCESS_ENDPOINTS = {
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/role-access/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	UPDATE: (id: string | number) => ({
		url: `/api/v1/role-access/${id}`,
		method: HTTP_METHODS.PUT,
		requiresAuth: true,
	}),
} as const;

// ROLE ENDPOINTS
export const ROLE_ENDPOINTS = {
	FORM_OPTIONS: { url: '/api/v1/role/form-options', method: HTTP_METHODS.GET, requiresAuth: true },
	CONSTANTS: { url: '/api/v1/role/constants', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ALL: {
		url: '/api/v1/role/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/role/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/role/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/role/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	UPDATE_STATUS: (id: string | number) => ({
		url: `/api/v1/role/update-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/role/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/role/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// COMMON CONSTANTS ENDPOINTS
export const COMMON_ENDPOINTS = {
	APP_ASSETS: {
		url: '/api/v1/common/app-assets',
		method: HTTP_METHODS.GET,
		requiresAuth: false,
	},
	CONSTANTS: {
		url: '/api/v1/common/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// ITEM TYPE ENDPOINTS
export const ITEM_TYPE_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/item-type/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/item-type/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/item-type/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/item-type/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/item-type/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/item-type/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/item-type/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// SIZE MASTER ENDPOINTS
export const SIZE_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/size/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/size/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/size/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/size/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/size/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/size/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/size/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// COLOUR MASTER ENDPOINTS
export const COLOUR_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/colour/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/colour/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/colour/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/colour/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/colour/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/colour/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/colour/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// PROCESS MASTER ENDPOINTS
export const PROCESS_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/process/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/process/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/process/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/process/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/process/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/process/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/process/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/process/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// FABRIC TYPE MASTER ENDPOINTS
export const FABRIC_TYPE_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/fabric-type/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/fabric-type/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/fabric-type/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/fabric-type/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/fabric-type/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/fabric-type/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/fabric-type/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// FABRIC STAGE MASTER ENDPOINTS
export const FABRIC_STAGE_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/fabric-stage/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/fabric-stage/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/fabric-stage/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/fabric-stage/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/fabric-stage/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/fabric-stage/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	GET_ACTIVE: {
		url: '/api/v1/fabric-stage/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// PARTY MASTER ENDPOINTS
export const PARTY_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/party/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/party/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/party/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/party/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/party/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/party/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONSTANTS: {
		url: '/api/v1/party/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	COMPANIES: {
		url: '/api/v1/party/companies',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/party/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// CUTTING COMPONENT MASTER ENDPOINTS
export const CUTTING_COMPONENT_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/cutting-component/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/cutting-component/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/cutting-component/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/cutting-component/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/cutting-component/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/cutting-component/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONSTANTS: {
		url: '/api/v1/cutting-component/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/cutting-component/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// LABOUR MASTER ENDPOINTS
export const LABOUR_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/labour/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/labour/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/labour/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/labour/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/labour/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/labour/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONSTANTS: {
		url: '/api/v1/labour/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/labour/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// ITEM MASTER ENDPOINTS
export const ITEM_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/item/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/item/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/item/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/item/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/item/delete-related/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/item/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONSTANTS: {
		url: '/api/v1/item/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/item/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// FABRIC MASTER ENDPOINTS
export const FABRIC_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/fabric/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/fabric/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/fabric/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/fabric/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/fabric/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	TOGGLE_STATUS: (id: string | number) => ({
		url: `/api/v1/fabric/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONSTANTS: {
		url: '/api/v1/fabric/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/fabric/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// FABRIC INWARD (PRODUCTION) ENDPOINTS
export const FABRIC_INWARD_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/fabric-inward/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/fabric-inward/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/fabric-inward/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/fabric-inward/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/fabric-inward/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/fabric-inward/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/fabric-inward/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/fabric-inward/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

// FABRIC STOCK (PRODUCTION) ENDPOINTS
export const FABRIC_STOCK_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/fabric-stock/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUMMARY: {
		url: '/api/v1/fabric-stock/summary',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// CUTTING STOCK (PRODUCTION) ENDPOINTS
export const CUTTING_STOCK_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/cutting-stock/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// READY STOCK (PRODUCTION)
export const READY_STOCK_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/ready-stock/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUMMARY: {
		url: '/api/v1/ready-stock/summary',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// DELIVERY CHALLAN (PRODUCTION)
export const DELIVERY_CHALLAN_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/delivery-challan/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	AVAILABLE_STOCK: {
		url: '/api/v1/delivery-challan/available-stock',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/delivery-challan/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/delivery-challan/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	PRINT: (id: string | number) => ({
		url: `/api/v1/delivery-challan/print/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/delivery-challan/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CANCEL: (id: string | number) => ({
		url: `/api/v1/delivery-challan/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

// LABOUR HISAAB (PRODUCTION)
export const LABOUR_HISAAB_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/labour-hisaab/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/labour-hisaab/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	WORK: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/get-one/${id}/work`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CUTTING: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/get-one/${id}/cutting`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	STATEMENT: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/get-one/${id}/statement`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	PAYMENTS: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/get-one/${id}/payments`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	PRINT: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/print/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	RECONCILE: {
		url: '/api/v1/labour-hisaab/reconcile',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	PAYMENT_ACCOUNTS: { url: '/api/v1/labour-hisaab/payment-accounts', method: HTTP_METHODS.GET, requiresAuth: true },
	CREATE: {
		url: '/api/v1/labour-hisaab/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CANCEL: (id: string | number) => ({
		url: `/api/v1/labour-hisaab/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	SALARY: { url: '/api/v1/labour-hisaab/salary', method: HTTP_METHODS.GET, requiresAuth: true },
	SALARY_PAYMENT_ACCOUNTS: { url: '/api/v1/labour-hisaab/salary/payment-accounts', method: HTTP_METHODS.GET, requiresAuth: true },
	SALARY_PERIOD: { url: '/api/v1/labour-hisaab/salary/period', method: HTTP_METHODS.POST, requiresAuth: true },
	SALARY_PERIOD_UPDATE: { url: '/api/v1/labour-hisaab/salary/period', method: HTTP_METHODS.PATCH, requiresAuth: true },
	SALARY_APPROVE: (id: string | number) => ({ url: `/api/v1/labour-hisaab/salary/period/${id}/approve`, method: HTTP_METHODS.PATCH, requiresAuth: true }),
	SALARY_PAYMENT: { url: '/api/v1/labour-hisaab/salary/payment', method: HTTP_METHODS.POST, requiresAuth: true },
	SALARY_CANCEL: (id: string | number) => ({ url: `/api/v1/labour-hisaab/cancel/salary/${id}`, method: HTTP_METHODS.PATCH, requiresAuth: true }),
} as const;

// CUTTING (PRODUCTION) ENDPOINTS
export const CUTTING_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/cutting/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/cutting/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/cutting/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/cutting/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/cutting/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/cutting/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	MAPPINGS: {
		url: '/api/v1/cutting/mappings',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// BUNDLING (PRODUCTION) ENDPOINTS
export const BUNDLING_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/bundling/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CONSTANTS: {
		url: '/api/v1/bundling/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	STOCK: {
		url: '/api/v1/bundling/stock',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ITEM_COLOUR_OPTIONS: {
		url: '/api/v1/bundling/item-colour-options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CREATE: {
		url: '/api/v1/bundling/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/bundling/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	UPDATE: (id: string | number) => ({
		url: `/api/v1/bundling/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/bundling/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL_BUNDLE: (id: string | number) => ({
		url: `/api/v1/bundling/cancel-bundle/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

// PROCESS ISSUE (PRODUCTION) ENDPOINTS
export const PROCESS_ISSUE_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/process-issue/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/process-issue/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/process-issue/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CANCEL: (id: string | number) => ({
		url: `/api/v1/process-issue/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	RELEASE_HOLD: (id: string | number) => ({
		url: `/api/v1/process-issue/release-hold/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	AVAILABLE_BUNDLES: {
		url: '/api/v1/process-issue/available-bundles',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	AVAILABLE_BUNDLES_BATCH: {
		url: '/api/v1/process-issue/available-bundles-batch',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SCAN_BUNDLES: {
		url: '/api/v1/process-issue/scan-bundles',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	RESOLVE_BUNDLE: {
		url: '/api/v1/process-issue/resolve-bundle',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUMMARY: {
		url: '/api/v1/process-issue/summary',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	HISTORY: {
		url: '/api/v1/process-issue/history',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SPLIT: {
		url: '/api/v1/process-issue/split',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	TRANSFER: {
		url: '/api/v1/process-issue/transfer',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CONSTANTS: {
		url: '/api/v1/process-issue/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ACTIVE: {
		url: '/api/v1/process-issue/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ACTIVE_LABOURS: {
		url: '/api/v1/process-issue/active-labours',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// PROCESS RECEIVE (PRODUCTION) ENDPOINTS
export const PROCESS_RECEIVE_ENDPOINTS = {
	PENDING: {
		url: '/api/v1/process-receive/pending',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	PENDING_BATCH: {
		url: '/api/v1/process-receive/pending-batch',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ALTER_OPTIONS: {
		url: '/api/v1/process-receive/alter-options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CREATE: {
		url: '/api/v1/process-receive/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CORRECT: {
		url: '/api/v1/process-receive/correct',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
} as const;

// VENDOR MASTER ENDPOINTS
export const VENDOR_ENDPOINTS = {
	GET_ACTIVE: {
		url: '/api/v1/vendor/get-active',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
} as const;

// LOGIN HISTORY ENDPOINTS
export const LOGIN_HISTORY_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/login-history/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_BY_USER: (userId: string | number) => ({
		url: `/api/v1/login-history/get-all/${userId}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
} as const;

export const SALES_ORDER_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/sales-order/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CUSTOMERS: {
		url: '/api/v1/sales-order/customers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ITEM_OPTIONS: {
		url: '/api/v1/sales-order/item-options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/sales-order/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/sales-order/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/sales-order/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/sales-order/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONFIRM: (id: string | number) => ({
		url: `/api/v1/sales-order/confirm/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/sales-order/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const SALES_INVOICE_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/sales-invoice/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CUSTOMERS: {
		url: '/api/v1/sales-invoice/customers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ITEM_OPTIONS: {
		url: '/api/v1/sales-invoice/item-options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPEN_CHALLANS: {
		url: '/api/v1/sales-invoice/open-challans',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	TAX_RATES: {
		url: '/api/v1/sales-invoice/tax-rates',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	NUMBER_SETTING: {
		url: '/api/v1/sales-invoice/number-setting',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: { url: '/api/v1/sales-invoice/get-all', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/sales-invoice/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: { url: '/api/v1/sales-invoice/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({
		url: `/api/v1/sales-invoice/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	SEND: (id: string | number) => ({
		url: `/api/v1/sales-invoice/send/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/sales-invoice/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	PRINT: (id: string | number) => ({
		url: `/api/v1/sales-invoice/print/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
} as const;

export const CUSTOMER_PAYMENT_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/customer-payment/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CUSTOMERS: {
		url: '/api/v1/customer-payment/customers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPEN_INVOICES: {
		url: '/api/v1/customer-payment/open-invoices',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/customer-payment/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/customer-payment/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/customer-payment/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CANCEL: (id: string | number) => ({
		url: `/api/v1/customer-payment/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const PURCHASE_ORDER_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/purchase-order/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUPPLIERS: {
		url: '/api/v1/purchase-order/suppliers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPTIONS: {
		url: '/api/v1/purchase-order/options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/purchase-order/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/purchase-order/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: { url: '/api/v1/purchase-order/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({
		url: `/api/v1/purchase-order/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CONFIRM: (id: string | number) => ({
		url: `/api/v1/purchase-order/confirm/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/purchase-order/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const PURCHASE_INVOICE_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/purchase-invoice/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUPPLIERS: {
		url: '/api/v1/purchase-invoice/suppliers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPTIONS: {
		url: '/api/v1/purchase-invoice/options',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPEN_INWARDS: {
		url: '/api/v1/purchase-invoice/open-inwards',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/purchase-invoice/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/purchase-invoice/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/purchase-invoice/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/purchase-invoice/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	SEND: (id: string | number) => ({
		url: `/api/v1/purchase-invoice/send/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/purchase-invoice/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	PRINT: (id: string | number) => ({
		url: `/api/v1/purchase-invoice/print/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
} as const;

export const SUPPLIER_PAYMENT_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/supplier-payment/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUPPLIERS: {
		url: '/api/v1/supplier-payment/suppliers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	OPEN_INVOICES: {
		url: '/api/v1/supplier-payment/open-invoices',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/supplier-payment/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/supplier-payment/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/supplier-payment/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	CANCEL: (id: string | number) => ({
		url: `/api/v1/supplier-payment/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const DEBIT_NOTE_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/debit-note/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	SUPPLIERS: {
		url: '/api/v1/debit-note/suppliers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CATALOG_OPTIONS: { url: '/api/v1/debit-note/catalog-options', method: HTTP_METHODS.GET, requiresAuth: true },
	OPEN_INVOICES: {
		url: '/api/v1/debit-note/open-invoices',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	APPLY_OPTIONS: (id: string | number) => ({
		url: `/api/v1/debit-note/${id}/apply-options`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	APPLY: (id: string | number) => ({
		url: `/api/v1/debit-note/${id}/apply`,
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	}),
	GET_ALL: { url: '/api/v1/debit-note/get-all', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/debit-note/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: { url: '/api/v1/debit-note/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({
		url: `/api/v1/debit-note/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	POST: (id: string | number) => ({
		url: `/api/v1/debit-note/send/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/debit-note/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const EXPENSE_CATEGORY_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/expense-categories/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ALL: {
		url: '/api/v1/expense-categories/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/expense-categories/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: {
		url: '/api/v1/expense-categories/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/expense-categories/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	TOGGLE: (id: string | number) => ({
		url: `/api/v1/expense-categories/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DELETE: (id: string | number) => ({
		url: `/api/v1/expense-categories/delete/${id}`,
		method: HTTP_METHODS.DELETE,
		requiresAuth: true,
	}),
} as const;

export const TAX_RATE_ENDPOINTS = {
	CONSTANTS: { url: '/api/v1/tax-rates/constants', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ALL: { url: '/api/v1/tax-rates/get-all', method: HTTP_METHODS.GET, requiresAuth: true },
	CREATE: { url: '/api/v1/tax-rates/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({ url: `/api/v1/tax-rates/update/${id}`, method: HTTP_METHODS.PATCH, requiresAuth: true }),
	TOGGLE: (id: string | number) => ({ url: `/api/v1/tax-rates/toggle-status/${id}`, method: HTTP_METHODS.PATCH, requiresAuth: true }),
	DELETE: (id: string | number) => ({ url: `/api/v1/tax-rates/delete/${id}`, method: HTTP_METHODS.DELETE, requiresAuth: true }),
} as const;

export const EXPENSE_ENDPOINTS = {
	CONSTANTS: { url: '/api/v1/expenses/constants', method: HTTP_METHODS.GET, requiresAuth: true },
	OPTIONS: { url: '/api/v1/expenses/options', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ALL: { url: '/api/v1/expenses/get-all', method: HTTP_METHODS.GET, requiresAuth: true },
	EXPORT: { url: '/api/v1/expenses/export', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/expenses/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: { url: '/api/v1/expenses/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({
		url: `/api/v1/expenses/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/expenses/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const BANK_ENDPOINTS = {
	CONSTANTS: { url: '/api/v1/bank/constants', method: HTTP_METHODS.GET, requiresAuth: true },
	ACTIVE: { url: '/api/v1/bank/active', method: HTTP_METHODS.GET, requiresAuth: true },
	ACCOUNTS: { url: '/api/v1/bank/accounts', method: HTTP_METHODS.GET, requiresAuth: true },
	ACCOUNT_CREATE: {
		url: '/api/v1/bank/accounts/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	ACCOUNT_UPDATE: (id: string | number) => ({
		url: `/api/v1/bank/accounts/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	ACCOUNT_TOGGLE: (id: string | number) => ({
		url: `/api/v1/bank/accounts/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DEPOSITS: { url: '/api/v1/bank/deposits', method: HTTP_METHODS.GET, requiresAuth: true },
	DEPOSIT_CREATE: {
		url: '/api/v1/bank/deposits',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	TRANSACTIONS: {
		url: '/api/v1/bank/transactions/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	TRANSACTION_PRINT: {
		url: '/api/v1/bank/transactions/print',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	TRANSACTION_CREATE: {
		url: '/api/v1/bank/transactions/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	TRANSACTION_CANCEL: (id: string | number) => ({
		url: `/api/v1/bank/transactions/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const PETTY_CASH_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/petty-cash/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	ACTIVE: { url: '/api/v1/petty-cash/active', method: HTTP_METHODS.GET, requiresAuth: true },
	ACCOUNTS: { url: '/api/v1/petty-cash/accounts', method: HTTP_METHODS.GET, requiresAuth: true },
	ACCOUNT_CREATE: {
		url: '/api/v1/petty-cash/accounts/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	ACCOUNT_UPDATE: (id: string | number) => ({
		url: `/api/v1/petty-cash/accounts/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	ACCOUNT_TOGGLE: (id: string | number) => ({
		url: `/api/v1/petty-cash/accounts/toggle-status/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	DEPOSITS: { url: '/api/v1/petty-cash/deposits', method: HTTP_METHODS.GET, requiresAuth: true },
	DEPOSIT_CREATE: {
		url: '/api/v1/petty-cash/deposits',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	TRANSACTIONS: {
		url: '/api/v1/petty-cash/transactions/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	TRANSACTION_PRINT: {
		url: '/api/v1/petty-cash/transactions/print',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	TRANSACTION_CREATE: {
		url: '/api/v1/petty-cash/transactions/create',
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	},
	TRANSACTION_CANCEL: (id: string | number) => ({
		url: `/api/v1/petty-cash/transactions/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

export const CREDIT_NOTE_ENDPOINTS = {
	CONSTANTS: {
		url: '/api/v1/credit-note/constants',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CUSTOMERS: {
		url: '/api/v1/credit-note/customers',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	CATALOG_OPTIONS: { url: '/api/v1/credit-note/catalog-options', method: HTTP_METHODS.GET, requiresAuth: true },
	TAX_RATES: { url: '/api/v1/credit-note/tax-rates', method: HTTP_METHODS.GET, requiresAuth: true },
	OPEN_INVOICES: {
		url: '/api/v1/credit-note/open-invoices',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	APPLY_OPTIONS: (id: string | number) => ({
		url: `/api/v1/credit-note/${id}/apply-options`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	APPLY: (id: string | number) => ({
		url: `/api/v1/credit-note/${id}/apply`,
		method: HTTP_METHODS.POST,
		requiresAuth: true,
	}),
	GET_ALL: { url: '/api/v1/credit-note/get-all', method: HTTP_METHODS.GET, requiresAuth: true },
	GET_ONE: (id: string | number) => ({
		url: `/api/v1/credit-note/get-one/${id}`,
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	}),
	CREATE: { url: '/api/v1/credit-note/create', method: HTTP_METHODS.POST, requiresAuth: true },
	UPDATE: (id: string | number) => ({
		url: `/api/v1/credit-note/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	POST: (id: string | number) => ({
		url: `/api/v1/credit-note/send/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	CANCEL: (id: string | number) => ({
		url: `/api/v1/credit-note/cancel/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
} as const;

// NOTIFICATION ENDPOINTS
export const NOTIFICATION_ENDPOINTS = {
	GET_ALL: {
		url: '/api/v1/notifications/get-all',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	UNREAD_COUNT: {
		url: '/api/v1/notifications/unread-count',
		method: HTTP_METHODS.GET,
		requiresAuth: true,
	},
	UPDATE: (id: string | number) => ({
		url: `/api/v1/notifications/update/${id}`,
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	}),
	MARK_ALL_READ: {
		url: '/api/v1/notifications/mark-all-read',
		method: HTTP_METHODS.PATCH,
		requiresAuth: true,
	},
} as const;

// COMBINED API ENDPOINTS
export const API_ENDPOINTS = {
	AUTH: AUTH_ENDPOINTS,
	COMPANY: COMPANY_ENDPOINTS,
	USERS: USERS_ENDPOINTS,
	DASHBOARD: DASHBOARD_ENDPOINTS,
	REPORTS: REPORTS_ENDPOINTS,
	PERMISSIONS: PERMISSION_ENDPOINTS,
	ROLES: ROLE_ENDPOINTS,
	ROLE_ACCESS: ROLE_ACCESS_ENDPOINTS,
	COMMON: COMMON_ENDPOINTS,
	ITEM: ITEM_ENDPOINTS,
	ITEM_TYPE: ITEM_TYPE_ENDPOINTS,
	SIZE: SIZE_ENDPOINTS,
	COLOUR: COLOUR_ENDPOINTS,
	PROCESS: PROCESS_ENDPOINTS,
	FABRIC: FABRIC_ENDPOINTS,
	FABRIC_TYPE: FABRIC_TYPE_ENDPOINTS,
	FABRIC_STAGE: FABRIC_STAGE_ENDPOINTS,
	PARTY: PARTY_ENDPOINTS,
	CUTTING_COMPONENT: CUTTING_COMPONENT_ENDPOINTS,
	LABOUR: LABOUR_ENDPOINTS,
	FABRIC_INWARD: FABRIC_INWARD_ENDPOINTS,
	FABRIC_STOCK: FABRIC_STOCK_ENDPOINTS,
	CUTTING_STOCK: CUTTING_STOCK_ENDPOINTS,
	READY_STOCK: READY_STOCK_ENDPOINTS,
	DELIVERY_CHALLAN: DELIVERY_CHALLAN_ENDPOINTS,
	SALES_ORDER: SALES_ORDER_ENDPOINTS,
	SALES_INVOICE: SALES_INVOICE_ENDPOINTS,
	CUSTOMER_PAYMENT: CUSTOMER_PAYMENT_ENDPOINTS,
	CREDIT_NOTE: CREDIT_NOTE_ENDPOINTS,
	PURCHASE_ORDER: PURCHASE_ORDER_ENDPOINTS,
	PURCHASE_INVOICE: PURCHASE_INVOICE_ENDPOINTS,
	SUPPLIER_PAYMENT: SUPPLIER_PAYMENT_ENDPOINTS,
	DEBIT_NOTE: DEBIT_NOTE_ENDPOINTS,
	EXPENSE_CATEGORY: EXPENSE_CATEGORY_ENDPOINTS,
	TAX_RATE: TAX_RATE_ENDPOINTS,
	EXPENSE: EXPENSE_ENDPOINTS,
	BANK: BANK_ENDPOINTS,
	PETTY_CASH: PETTY_CASH_ENDPOINTS,
	LABOUR_HISAAB: LABOUR_HISAAB_ENDPOINTS,
	CUTTING: CUTTING_ENDPOINTS,
	BUNDLING: BUNDLING_ENDPOINTS,
	PROCESS_ISSUE: PROCESS_ISSUE_ENDPOINTS,
	PROCESS_RECEIVE: PROCESS_RECEIVE_ENDPOINTS,
	VENDOR: VENDOR_ENDPOINTS,
	LOGIN_HISTORY: LOGIN_HISTORY_ENDPOINTS,
	NOTIFICATION: NOTIFICATION_ENDPOINTS,
} as const;

export default API_ENDPOINTS;
